import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/Button';

const Hero: React.FC = () => {
  return (
    <section className="relative bg-background overflow-hidden" id="home">
      <div className="max-w-container mx-auto">
        <div className="relative z-10 pb-8 bg-background sm:pb-16 md:pb-20 lg:max-w-2xl lg:w-full lg:pb-28 xl:pb-32">
          <svg
            className="hidden lg:block absolute right-0 inset-y-0 h-full w-48 text-background transform translate-x-1/2"
            fill="currentColor"
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <polygon points="50,0 100,0 50,100 0,100" />
          </svg>

          <div className="relative pt-6 px-4 sm:px-6 lg:px-8"></div>

          <main className="mt-10 mx-auto max-w-container px-4 sm:mt-12 sm:px-6 md:mt-16 lg:mt-20 lg:px-8 xl:mt-28">
            <div className="sm:text-center lg:text-left">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl tracking-tight font-extrabold text-text sm:text-4xl md:text-5xl lg:text-6xl"
              >
                <span className="block xl:inline">Reason Shield:</span>{' '}
                <span className="block text-primary xl:inline">Molecular & PGx Testing Platform</span>
              </motion.h1>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-3 text-xl font-bold text-text sm:mt-5 sm:text-2xl sm:max-w-xl sm:mx-auto md:mt-5 md:text-2xl lg:mx-0"
              >
                Advanced Molecular & Pharmacogenomics Testing Platform
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-3 text-base text-text sm:mt-5 sm:text-lg sm:max-w-xl sm:mx-auto md:mt-5 md:text-xl lg:mx-0"
              >
                ReasonShield transforms complex laboratory data into comprehensive, interactive, and clinically valuable reports. Our molecular platform provides pathogen detection, antimicrobial resistance analysis, and evidence-based treatment recommendations, while our PGx platform delivers personalized pharmacogenomic insights for precision medicine.
              </motion.p>
              <div className="mt-5 sm:mt-8 flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-center lg:justify-start">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.4 }}
                  className="shadow w-full sm:w-auto"
                >
                  <Button
                    href="/contact"
                    variant="primary"
                    size="md"
                    className="w-full sm:w-auto"
                  >
                    Schedule a Demo
                  </Button>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                  className="sm:ml-3 w-full sm:w-auto"
                >
                  <Button
                    href="/reports"
                    variant="outline"
                    size="md"
                    className="w-full sm:w-auto"
                  >
                    View All Reports
                  </Button>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                  className="sm:ml-3 w-full sm:w-auto"
                >
                  <Button
                    href="/report"
                    variant="secondary"
                    size="md"
                    className="w-full sm:w-auto"
                  >
                    View Demo Report
                  </Button>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 1.0 }}
                  className="sm:ml-3 w-full sm:w-auto"
                >
                  <Button
                    href="https://pgx.reasonshield.com"
                    variant="outline"
                    size="md"
                    className="w-full sm:w-auto"
                    isExternal
                  >
                    Explore PGx Platform
                  </Button>
                </motion.div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <div className="lg:absolute lg:inset-y-0 lg:right-0 lg:w-1/2">
        <img
          className="h-56 w-full object-cover sm:h-72 md:h-96 lg:w-full lg:h-full"
          src="https://images.unsplash.com/photo-1579154204601-01588f351e67?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80"
          alt="Laboratory Analysis Dashboard"
        />
      </div>
    </section>
  );
};

export default Hero; 